import type { CompleteOrganizationAsCompanyBody } from '@zrsystem/contracts';
import { Button, Checkbox, CheckboxRow, FormField, FormGrid, Input, Select } from '@zrsystem/ui';
import { useState } from 'react';

import { lookupPlatformCnpj } from '@/features/platform/api/lookup-cnpj.api.js';
import {
  formatCnpjFieldValue,
  isValidCnpjDigits,
  normalizeCnpjDigits,
} from '@/features/platform/form/parse-cnpj.js';
import { PLACE_TAX_REGIMES } from '@/features/platform/form/place-form.js';
import { HttpError } from '@/shared/api/types.js';
import { ContextualFeedback } from '@/shared/feedback/index.js';
import { translateError } from '@/shared/i18n/error-messages.js';

export interface CompleteOrganizationAsCompanyFormProps {
  enableReceitaLookup?: boolean;
  isSubmitting?: boolean;
  onSubmit: (body: CompleteOrganizationAsCompanyBody) => Promise<void>;
  onCancel: () => void;
}

export function CompleteOrganizationAsCompanyForm({
  enableReceitaLookup = false,
  isSubmitting = false,
  onSubmit,
  onCancel,
}: CompleteOrganizationAsCompanyFormProps) {
  const [name, setName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [taxRegime, setTaxRegime] = useState<CompleteOrganizationAsCompanyBody['taxRegime'] | ''>(
    '',
  );
  const [confirmed, setConfirmed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const handleDocumentChange = (value: string) => {
    const formatted = formatCnpjFieldValue(value);
    setDocumentNumber(formatted);
    const digits = normalizeCnpjDigits(formatted);
    if (!enableReceitaLookup || digits.length !== 14 || !isValidCnpjDigits(digits)) {
      return;
    }
    void lookupPlatformCnpj(digits)
      .then((preview) => {
        if (preview.legalName.trim().length > 0) {
          setName(preview.legalName);
        }
        if (preview.tradeName !== null && preview.tradeName.trim().length > 0) {
          setTradeName(preview.tradeName);
        }
      })
      .catch(() => {
        setFormError(null);
      });
  };

  const handleSubmit = async () => {
    const digits = normalizeCnpjDigits(documentNumber);
    const nextErrors: Record<string, string> = {};
    if (!isValidCnpjDigits(digits)) {
      nextErrors['documentNumber'] = 'CNPJ inválido. Verifique os dígitos.';
    }
    if (name.trim().length === 0) {
      nextErrors['name'] = 'Informe a razão social.';
    }
    if (taxRegime === '') {
      nextErrors['taxRegime'] = 'Selecione o regime tributário.';
    }
    if (!confirmed) {
      nextErrors['confirmed'] = 'Confirme a conclusão do cadastro.';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || taxRegime === '') {
      return;
    }

    setFormError(null);
    try {
      await onSubmit({
        name: name.trim(),
        documentNumber: digits,
        taxRegime,
        confirmed: true,
        ...(tradeName.trim().length > 0 ? { tradeName: tradeName.trim() } : {}),
      });
    } catch (error) {
      if (error instanceof HttpError) {
        setFormError(translateError(error.code));
        return;
      }
      setFormError('Não foi possível concluir o cadastro como pessoa jurídica.');
    }
  };

  return (
    <div className="complete-organization-as-company">
      {formError ? <ContextualFeedback variant="danger" message={formError} /> : null}
      <FormGrid>
        <FormField kind="document" label="CNPJ" required error={fieldErrors['documentNumber']}>
          <Input
            aria-label="CNPJ da conclusão"
            value={documentNumber}
            onChange={(event) => {
              handleDocumentChange(event.target.value);
            }}
            inputMode="numeric"
          />
        </FormField>
        <FormField kind="name" label="Razão social" required error={fieldErrors['name']}>
          <Input
            aria-label="Razão social da conclusão"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </FormField>
        <FormField kind="name" label="Nome fantasia">
          <Input
            aria-label="Nome fantasia da conclusão"
            value={tradeName}
            onChange={(event) => {
              setTradeName(event.target.value);
            }}
          />
        </FormField>
        <FormField
          kind="taxonomy"
          label="Regime tributário"
          required
          error={fieldErrors['taxRegime']}
        >
          <Select
            aria-label="Regime tributário da conclusão"
            value={taxRegime}
            onChange={(event) => {
              setTaxRegime(event.target.value as CompleteOrganizationAsCompanyBody['taxRegime']);
            }}
          >
            <option value="">Selecione</option>
            {PLACE_TAX_REGIMES.map((value) => (
              <option key={value} value={value}>
                {value === 'SIMPLES_NACIONAL'
                  ? 'Simples Nacional'
                  : value === 'LUCRO_PRESUMIDO'
                    ? 'Lucro presumido'
                    : 'Lucro real'}
              </option>
            ))}
          </Select>
        </FormField>
        <CheckboxRow>
          <Checkbox
            checked={confirmed}
            onChange={(event) => {
              setConfirmed(event.target.checked);
            }}
          />
          Confirmo a troca do CPF pelo CNPJ. Essa passagem não volta para pessoa física.
        </CheckboxRow>
      </FormGrid>
      {fieldErrors['confirmed'] ? (
        <ContextualFeedback variant="warning" message={fieldErrors['confirmed']} />
      ) : null}
      <div className="qp-drawer-footer-actions">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={isSubmitting}
          onClick={() => {
            void handleSubmit();
          }}
        >
          {isSubmitting ? 'Concluindo…' : 'Concluir como pessoa jurídica'}
        </Button>
      </div>
    </div>
  );
}
